/**
 * General Info Page
 * Dealer profile/settings: Name, Address, Phone, Primary Contact.
 * Uses existing endpoints: GET/PUT /settings/business, GET/PUT /settings/dealer-location.
 */
import { useState, useEffect, useMemo } from "react";
import { useAuth } from "../contexts/AuthContext";
import { useToast } from "../contexts/ToastContext";
import { Save, Info, MapPin, Phone, User as UserIcon, Car } from "lucide-react";
import franchiseMakesModels from "../data/franchise_makes_models.json";

const GeneralInfoPage = () => {
  const { apiFetch } = useAuth();
  const toast = useToast();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [name, setName] = useState("");
  const [franchiseMake, setFranchiseMake] = useState("");
  const [address, setAddress] = useState({
    dealer_street: "",
    dealer_city: "",
    dealer_state: "",
    dealer_zip: "",
  });
  // Phone + Contact fields: stored on business row server-side; read-only here
  // until backend exposes dealer_phone/contact_* on /settings/business.
  const [phone] = useState("");
  const [contactName] = useState("");
  const [contactEmail] = useState("");

  const franchiseMakes = useMemo(
    () => Object.keys(franchiseMakesModels).sort((a, b) => a.localeCompare(b)),
    []
  );

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const [bizRes, locRes] = await Promise.all([
          apiFetch("/settings/business", { method: "GET" }),
          apiFetch("/settings/dealer-location", { method: "GET" }).catch(() => ({ data: {} })),
        ]);
        if (!alive) return;
        setName(bizRes.data?.name || "");
        setFranchiseMake(bizRes.data?.franchise_make || "");
        setAddress({
          dealer_street: locRes.data?.dealer_street || "",
          dealer_city: locRes.data?.dealer_city || "",
          dealer_state: locRes.data?.dealer_state || "",
          dealer_zip: locRes.data?.dealer_zip || "",
        });
      } catch (e) {
        toast.error("Failed to load general info");
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, [apiFetch, toast]);

  const handleSave = async () => {
    setSaving(true);
    try {
      // Name + franchise_make update — settings API uses query params
      const qs = new URLSearchParams({ name, franchise_make: franchiseMake });
      await apiFetch(`/settings/business?${qs.toString()}`, { method: "PUT" });
      // Address update
      await apiFetch("/settings/dealer-location", { method: "PUT", data: address });
      toast.success("General info saved");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to save");
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <div className="loading">Loading…</div>;

  return (
    <div className="p-6 max-w-[1100px] mx-auto" data-testid="general-info-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">General Info</h1>
          <p className="text-gray-500 text-sm mt-0.5">Your dealership profile and contact details</p>
        </div>
        <button
          className="inline-flex items-center gap-2 bg-purple-600 hover:bg-purple-700 text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors disabled:opacity-50"
          onClick={handleSave}
          disabled={saving}
          data-testid="save-general-info-btn"
        >
          <Save size={14} /> {saving ? "Saving…" : "Save Changes"}
        </button>
      </div>

      <div className="grid grid-cols-1 gap-6">
        {/* Dealership Info */}
        <section className="bg-white rounded-xl border border-gray-200 p-6" data-testid="dealership-info-section">
          <div className="flex items-center gap-2 mb-5 pb-3 border-b border-gray-100">
            <Info size={16} className="text-purple-600" />
            <h2 className="text-sm font-semibold text-gray-900">Dealership Information</h2>
          </div>
          <div className="space-y-4">
            <Field label="Dealership Name">
              <input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                placeholder="e.g. Verified Demand Ford"
                data-testid="general-info-name"
              />
            </Field>
            <Field label="Franchise Make">
              <div className="relative">
                <Car size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
                <select
                  value={franchiseMake}
                  onChange={(e) => setFranchiseMake(e.target.value)}
                  className="w-full pl-9 pr-3 py-2 border border-gray-300 rounded-lg text-sm bg-white focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                  data-testid="general-info-franchise-make"
                >
                  <option value="">Select franchise make…</option>
                  {franchiseMakes.map((m) => (
                    <option key={m} value={m}>{m}</option>
                  ))}
                </select>
              </div>
              <p className="text-xs text-gray-500 mt-1.5">
                Required for New vehicle pricing rules. This is your primary OEM franchise.
              </p>
            </Field>
          </div>
        </section>

        {/* Address */}
        <section className="bg-white rounded-xl border border-gray-200 p-6" data-testid="address-section">
          <div className="flex items-center gap-2 mb-3 pb-3 border-b border-gray-100">
            <MapPin size={16} className="text-purple-600" />
            <h2 className="text-sm font-semibold text-gray-900">Address</h2>
          </div>
          <div className="flex items-start gap-2 mb-5 p-3 rounded-lg bg-blue-50 border border-blue-100" data-testid="address-geo-note">
            <Info size={14} className="text-blue-600 mt-0.5 flex-shrink-0" />
            <p className="text-xs text-blue-800 leading-relaxed">
              This address powers <strong>Geographic Demand</strong> proximity analysis &mdash; nearby ZIP codes are ranked by distance from this location.
              This is the single source of truth for your dealership address.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-6 gap-4">
            <div className="md:col-span-6">
              <Field label="Street Address">
                <input
                  type="text"
                  value={address.dealer_street}
                  onChange={(e) => setAddress(prev => ({ ...prev, dealer_street: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                  placeholder="123 Main Street"
                  data-testid="general-info-street"
                />
              </Field>
            </div>
            <div className="md:col-span-3">
              <Field label="City">
                <input
                  type="text"
                  value={address.dealer_city}
                  onChange={(e) => setAddress(prev => ({ ...prev, dealer_city: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                  placeholder="Los Angeles"
                  data-testid="general-info-city"
                />
              </Field>
            </div>
            <div className="md:col-span-1">
              <Field label="State">
                <input
                  type="text"
                  value={address.dealer_state}
                  onChange={(e) => setAddress(prev => ({ ...prev, dealer_state: e.target.value.toUpperCase() }))}
                  maxLength={2}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                  placeholder="CA"
                  data-testid="general-info-state"
                />
              </Field>
            </div>
            <div className="md:col-span-2">
              <Field label="ZIP Code">
                <input
                  type="text"
                  value={address.dealer_zip}
                  onChange={(e) => setAddress(prev => ({ ...prev, dealer_zip: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                  placeholder="90001"
                  data-testid="general-info-zip"
                />
              </Field>
            </div>
          </div>
        </section>

        {/* Contact */}
        <section className="bg-white rounded-xl border border-gray-200 p-6" data-testid="contact-section">
          <div className="flex items-center gap-2 mb-1 pb-3 border-b border-gray-100">
            <Phone size={16} className="text-purple-600" />
            <h2 className="text-sm font-semibold text-gray-900">Phone & Primary Contact</h2>
          </div>
          <p className="text-xs text-gray-500 mb-4 mt-3">Contact your Verified Demand administrator to update phone and primary contact fields.</p>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <Field label="Phone">
              <input
                type="text"
                value={phone}
                readOnly
                disabled
                className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm bg-gray-50 text-gray-500"
                placeholder="(not set)"
                data-testid="general-info-phone"
              />
            </Field>
            <Field label="Primary Contact Name">
              <input
                type="text"
                value={contactName}
                readOnly
                disabled
                className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm bg-gray-50 text-gray-500"
                placeholder="(not set)"
                data-testid="general-info-contact-name"
              />
            </Field>
            <Field label="Primary Contact Email">
              <input
                type="email"
                value={contactEmail}
                readOnly
                disabled
                className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm bg-gray-50 text-gray-500"
                placeholder="(not set)"
                data-testid="general-info-contact-email"
              />
            </Field>
          </div>
        </section>
      </div>
    </div>
  );
};

const Field = ({ label, children }) => (
  <label className="block">
    <span className="block text-xs font-semibold text-gray-600 mb-1.5 tracking-wide uppercase">{label}</span>
    {children}
  </label>
);

export default GeneralInfoPage;
